import { useState } from 'react';

import withStyles from '@mui/styles/withStyles';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { Card } from '../../../cards';
import {
  LearningContentListItem,
  MetadataDuration,
  learningContentDefaultTranslations,
} from '../../../learning-content';
import { palette } from '../../../theme';
import Illustration from '../Illustration';
import PathCertification from '../PathCertification';

export const styles = /* istanbul ignore next */ ({ spacing, breakpoints, typography }) => {
  const subtitle1 = { ...typography.subtitle1 };
  const h5 = { ...typography.h5 };

  delete subtitle1.color;
  delete h5.color;
  delete subtitle1[breakpoints.down('sm')]?.color;
  delete h5[breakpoints.down('sm')]?.color;

  return {
    jobGuarantee: {
      display: 'none',
      [breakpoints.up('sm')]: {
        display: 'initial',
      },
    },
    description: {
      display: 'none',
      [breakpoints.up('sm')]: {
        display: 'inherit',
      },
    },
    illustration: {
      paddingBottom: '56.25%',
    },
    releaseDate: {
      ...subtitle1,
      [breakpoints.up('sm')]: {
        ...h5,
      },
    },
    noWrap: {
      whiteSpace: 'nowrap',
    },
    cardContent: {
      '& a': {
        textDecoration: 'none',
        '&:hover': {
          color: 'inherit',
        },
      },
      padding: 0,
      '&:last-child': {
        paddingBottom: 0,
      },
    },
    informationContainer: {
      [breakpoints.down('sm')]: {
        margin: [[0, 0, 0, spacing(2)]],
      },
    },
    listItemInnerHover: {
      '& $informationContainer': {
        color: 'inherit',
      },
    },
    listItemInner: {
      padding: spacing(2),
      [breakpoints.down('sm')]: {
        paddingBottom: spacing(2),
      },
    },
    metadataWrapper: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: [[spacing(1), 0]],
      [breakpoints.down('sm')]: {
        flexDirection: 'column',
        paddingLeft: spacing(2),
      },
    },
    metadata: {
      color: 'white',
    },
    clickable: {
      '&:hover': {
        cursor: 'pointer',
        background: 'inherit',
      },
    },
  };
};

export const PathCardHorizontal = ({
  certification = null,
  classes = {},
  duration = '',
  title,
  children = null,
  categoryColor,
  openedAt = null,
  className = '',
  href = null,
  locale = 'en',

  translations = {
    certification: 'Diploma',
    partners: 'Partners',
    durationFullTimeMonths: learningContentDefaultTranslations.durationFullTimeMonths,
    ...learningContentDefaultTranslations,
  },
  ...otherProps
}) => {
  const [isRaised, setRaised] = useState(false);
  const metadata = [];

  const isActive = !!openedAt;
  const isClickable = isActive && isRaised;

  if (certification) {
    metadata.push(
      <PathCertification
        key="PathCertification"
        classes={{ icon: classes.metadata, text: classes.metadata }}
        translations={{
          accessibilityLabel: translations.certification,
        }}
      >
        {certification}
      </PathCertification>
    );
  }

  metadata.push(
    <MetadataDuration
      key="duration"
      classes={{ icon: classes.metadata, text: classes.metadata }}
      className={classes.duration}
      shouldNotWrap
      duration={duration}
      translations={{
        ...translations,
        durationMonths: translations.durationFullTimeMonths,
      }}
      icon={CalendarTodayIcon}
    />
  );

  const MetadataWrapper = href ? 'a' : 'div';

  return (
    <Card
      raised={isClickable}
      onMouseEnter={() => setRaised(true)}
      onMouseLeave={() => setRaised(false)}
      onFocus={() => setRaised(true)}
      onBlur={() => setRaised(false)}
      classes={{ content: classes.cardContent }}
      className={className}
    >
      <LearningContentListItem
        illustrationComponent={Illustration}
        title={title}
        classes={{
          releaseDate: classes.releaseDate,
          illustrationContainer: classes.illustrationContainer,
          illustration: classes.illustration,
          informationContainer: classes.informationContainer,
          listItemInner: classes.listItemInner,
          listItemInnerHover: `${classes.listItemInnerHover} ${
            isClickable ? classes.clickable : ''
          }`,
          content: classes.description,
        }}
        categoryColor={categoryColor}
        openedAt={openedAt}
        href={href}
        locale={locale}
        translations={translations}
        {...otherProps}
      >
        <span className={classes.description}>{children}</span>
      </LearningContentListItem>
      <MetadataWrapper
        href={href}
        style={{ backgroundColor: isActive ? categoryColor : palette.text.inactive }}
        className={`${classes.metadataWrapper} ${isClickable ? classes.clickable : ''}`}
      >
        {metadata}
      </MetadataWrapper>
    </Card>
  );
};

export default withStyles(styles)(PathCardHorizontal);
